/**
 * agentury.js — nájezdy bloků stránky Pro agentury (`/cs/pro-agentury/`).
 *
 * Předloha: skript na konci `navrh-pro-agentury.html` („Bloky stránky:
 * animace po vjetí do výřezu"). Samostatný soubor, NE modul hlavního
 * balíku `za2026.js`: zařazuje ho `inc/agentury-2026.php` jen na této
 * stránce. Build: `npm run build:js` → `assets/js/dist/agentury.js`.
 *
 * Co dělá:
 *   1. Přidá `<html>` třídu `ag-reveal`. Teprve pod ní CSS skrývá bloky
 *      před nájezdem — bez skriptu je obsah vidět rovnou.
 *   2. IntersectionObserver přidá sekci třídu `in`, jakmile z ní je ve
 *      výřezu 6 % (práh z návrhu), a přestane ji sledovat.
 *   3. Po doběhnutí `ag-rise` dostane dlaždice/karta třídu `agdone`
 *      a vypadne z animačního pravidla — `forwards` by jinak držel
 *      `transform` a přebil hover.
 *
 * Mapa lokalit (`.loksec`) se tu NEsleduje: obsluhuje ji modul `reveal`
 * hlavního balíku stejně jako na úvodní stránce.
 */

const SEKCE = '.plist, .typy, .planb, .rezimy, .akce, .proof, .faq';
const KARTY = '.plist .prog, .typy .ttile, .rezimy .rcard, .rezimy .tri > div, .proof .dl > div, .faq details.acc';

function start() {
	const sekce = Array.prototype.slice.call( document.querySelectorAll( SEKCE ) );
	if ( ! sekce.length ) {
		return;
	}

	document.documentElement.classList.add( 'ag-reveal' );

	if ( ! ( 'IntersectionObserver' in window ) ) {
		sekce.forEach( ( s ) => s.classList.add( 'in' ) );
		return;
	}

	const io = new IntersectionObserver(
		( zaznamy ) => {
			zaznamy.forEach( ( z ) => {
				if ( z.isIntersecting ) {
					z.target.classList.add( 'in' );
					io.unobserve( z.target );
				}
			} );
		},
		{ threshold: 0.06 }
	);
	sekce.forEach( ( s ) => io.observe( s ) );

	document.addEventListener( 'animationend', ( e ) => {
		const t = e.target;
		if ( ! t || ! t.matches || 'ag-rise' !== e.animationName ) {
			return;
		}
		if ( t.matches( KARTY ) ) {
			t.classList.add( 'agdone' );
		}
	} );
}

/*
 * BLOK PRAHA (EN, DE, PL; `#prague`). Předloha `build-blok-praha.py`
 * (`JS_PRAHA`): V11 = šikmý červený pruh přetáhne hlavní fotku přes fotku
 * pozadí (0,6 s, jednou při ≥ 50 % fotky ve výřezu) a pak Ken Burns
 * 1 → 1,07 za 2,2 s; T09 = čísla `.prstats .cnt` se napočítají (1,4 s).
 * Bez IntersectionObserveru a s omezeným pohybem nic neběží — CSS drží
 * konečný stav (hlavní fotka celá, čísla konečná). Počáteční stavy visí
 * na třídě `pr-anim`, kterou přidá až tenhle skript.
 */
function praha() {
	const sec = document.getElementById( 'prague' );
	if ( ! sec || ! ( 'IntersectionObserver' in window ) ) {
		return;
	}
	if ( window.matchMedia && window.matchMedia( '(prefers-reduced-motion: reduce)' ).matches ) {
		return;
	}
	sec.classList.add( 'pr-anim' );
	const ease2io = ( t ) => ( t < 0.5 ? 2 * t * t : 1 - Math.pow( -2 * t + 2, 2 ) / 2 );
	const ease2o = ( t ) => 1 - ( 1 - t ) * ( 1 - t );
	const sineio = ( t ) => -( Math.cos( Math.PI * t ) - 1 ) / 2;

	const ph = sec.querySelector( '.v11' );
	const top = sec.querySelector( '.v11-top' );
	const bar = sec.querySelector( '.v11-bar' );
	if ( ph && top && bar ) {
		const UHEL = ( 4.45 * Math.PI ) / 180;
		const klip = ( p ) => {
			const w = ph.clientWidth;
			const h = ph.clientHeight;
			const sk = Math.tan( UHEL ) * w;
			const y = p * ( h + sk );
			top.style.clipPath = 'polygon(0px 0px, ' + w + 'px 0px, ' + w + 'px ' + ( y - sk ).toFixed( 1 ) + 'px, 0px ' + y.toFixed( 1 ) + 'px)';
			bar.style.transform = 'translateY(' + ( y - 7 + 60 * Math.tan( UHEL ) ).toFixed( 1 ) + 'px) rotate(-4.45deg)';
		};
		const topImg = top.querySelector( 'img' );
		const imgs = Array.prototype.slice.call( ph.querySelectorAll( 'img' ) );
		// Bez fotky pozadí není co přetahovat: hlavní fotka zůstane celá.
		if ( imgs.length > 1 && topImg ) {
			klip( 0 );
			// Past: oříznutou lazy fotku (nulová plocha) Chrome nikdy nestáhne → před blokem na eager.
			const ioL = new IntersectionObserver(
				( es ) => {
					if ( es[ 0 ].isIntersecting ) {
						ioL.disconnect();
						imgs.forEach( ( i ) => {
							i.loading = 'eager';
						} );
					}
				},
				{ rootMargin: '800px 0px' }
			);
			ioL.observe( sec );
			const ukaz = () => {
				top.style.clipPath = 'none';
				bar.style.visibility = 'hidden';
			};
			const nacti = ( i ) =>
				i.complete && i.naturalWidth
					? Promise.resolve()
					: new Promise( ( r ) => {
						i.addEventListener( 'load', r, { once: true } );
						i.addEventListener( 'error', r, { once: true } );
					} );
			const kb = () => {
				const t0 = window.performance.now();
				const k = ( now ) => {
					const t = Math.min( 1, ( now - t0 ) / 2200 );
					top.style.transform = 'scale(' + ( 1 + 0.07 * sineio( t ) ).toFixed( 4 ) + ')';
					if ( t < 1 ) {
						requestAnimationFrame( k );
					}
				};
				requestAnimationFrame( k );
			};
			let hotovo = false;
			const wipe = () => {
				if ( hotovo ) {
					return;
				}
				hotovo = true;
				imgs.forEach( ( i ) => {
					i.loading = 'eager';
				} );
				const dek = Promise.all( imgs.map( ( i ) => nacti( i ).then( () => ( i.decode ? i.decode().catch( () => 0 ) : 0 ) ) ) );
				Promise.race( [ dek, new Promise( ( r ) => setTimeout( r, 1500 ) ) ] ).then( () => {
					if ( ! topImg.complete || ! topImg.naturalWidth ) {
						ukaz();
						return;
					}
					setTimeout( () => {
						const t0 = window.performance.now();
						bar.style.visibility = 'visible';
						const k = ( now ) => {
							const t = Math.min( 1, ( now - t0 ) / 600 );
							klip( ease2io( t ) );
							if ( t < 1 ) {
								requestAnimationFrame( k );
							} else {
								ukaz();
								kb();
							}
						};
						requestAnimationFrame( k );
					}, 200 );
				} );
			};
			const ioF = new IntersectionObserver(
				( es ) => {
					if ( es[ 0 ].isIntersecting ) {
						ioF.disconnect();
						wipe();
					}
				},
				{ threshold: 0.5 }
			);
			ioF.observe( ph );
			window.addEventListener( 'resize', () => {
				if ( ! hotovo ) {
					klip( 0 );
				}
			} );
		}
	}

	const rada = sec.querySelector( '.prstats' );
	const cnts = Array.prototype.slice.call( sec.querySelectorAll( '.prstats .cnt' ) );
	if ( ! rada || ! cnts.length ) {
		return;
	}
	cnts.forEach( ( c ) => c.setAttribute( 'data-txt', c.textContent ) );
	const fmt = ( c, v ) => {
		const s = v.toFixed( +( c.getAttribute( 'data-dec' ) || 0 ) );
		return /,/.test( c.getAttribute( 'data-txt' ) ) ? s.replace( '.', ',' ) : s;
	};
	cnts.forEach( ( c ) => {
		c.textContent = fmt( c, 0 );
	} );
	const ioS = new IntersectionObserver(
		( es ) => {
			if ( ! es[ 0 ].isIntersecting ) {
				return;
			}
			ioS.disconnect();
			const t0 = window.performance.now() + 200;
			const k = ( now ) => {
				const t = Math.min( 1, Math.max( 0, ( now - t0 ) / 1400 ) );
				const e = ease2o( t );
				cnts.forEach( ( c ) => {
					c.textContent = fmt( c, +c.getAttribute( 'data-to' ) * e );
				} );
				if ( t < 1 ) {
					requestAnimationFrame( k );
				} else {
					cnts.forEach( ( c ) => {
						c.textContent = c.getAttribute( 'data-txt' );
					} );
				}
			};
			requestAnimationFrame( k );
		},
		{ threshold: 0.6 }
	);
	ioS.observe( rada );
}

const spust = () => {
	start();
	praha();
};

if ( 'loading' === document.readyState ) {
	document.addEventListener( 'DOMContentLoaded', spust );
} else {
	spust();
}
